import { useRef, useState } from 'react';
import { Bookmark, Loader2, X } from 'lucide-react';
import { useVersionStore } from '../../stores/versionStore';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { toast } from '../../lib/toast';

// Der Inhalt wird nur gemountet, solange der Dialog offen ist; so
// beginnt jedes Öffnen mit leerem Feld, ohne Zustand im Effect zurückzusetzen.
export default function NameVersionDialog() {
  const open = useVersionStore((s) => s.nameDialogOpen);
  return open ? <DialogBody /> : null;
}

function DialogBody() {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const close = () => { if (!busy) useVersionStore.getState().closeNameDialog(); };
  useFocusTrap(ref, true, close);

  const trimmed = label.trim();

  const submit = async (e) => {
    e?.preventDefault();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await useVersionStore.getState().nameVersion(trimmed);
      toast.success(`Stand „${trimmed}“ gespeichert.`);
      useVersionStore.getState().closeNameDialog();
    } catch (err) {
      setError(err.message || 'Stand konnte nicht gespeichert werden.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="name-version-title">
      <form ref={ref} onSubmit={submit} className="bg-surface rounded-2xl shadow-2xl border border-line w-full max-w-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-line flex items-center justify-between">
          <h3 id="name-version-title" className="font-bold text-ink text-base flex items-center gap-2">
            <Bookmark size={16} className="text-brand" aria-hidden="true" /> Stand benennen
          </h3>
          <button type="button" onClick={close} aria-label="Schließen" className="p-1 text-ink-3 hover:text-ink"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 flex flex-col gap-2">
          <label htmlFor="version-label" className="text-xs font-semibold text-ink-2">Name des Stands</label>
          <input
            id="version-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={80}
            placeholder="z.B. Rev. A an Kunde"
            className="bg-surface-2 border border-line text-ink rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-soft"
          />
          <p className="text-[11px] text-ink-4">Offene Änderungen werden vorher gespeichert.</p>
          {error && <p role="alert" className="text-[11px] text-err">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-line flex justify-end gap-2">
          <button type="button" onClick={close} className="px-4 py-2 text-xs font-bold text-ink-2 hover:bg-surface-2 rounded-md">Abbrechen</button>
          <button type="submit" disabled={!trimmed || busy} className="px-4 py-2 text-xs font-bold text-white bg-brand rounded-md disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <Loader2 size={13} className="animate-spin" aria-hidden="true" />}Speichern
          </button>
        </div>
      </form>
    </div>
  );
}
